import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnico } from '../../hooks/useUnico';
import './CapturePage.css';

type DeviceViewport = { width: number; height: number };

const DEVICE_VIEWPORTS: Record<string, DeviceViewport> = {
  'iphone-16-pro': { width: 402, height: 874 },
  'iphone-14': { width: 390, height: 844 },
  'iphone-se-3': { width: 375, height: 667 },
  'pixel-8': { width: 412, height: 915 },
  'pixel-7a': { width: 412, height: 915 },
  'samsung-s24': { width: 360, height: 780 },
  'samsung-s24u': { width: 412, height: 915 },
  'moto-g84': { width: 390, height: 844 },
  'redmi-note-13': { width: 392, height: 873 },
  'poco-x6': { width: 412, height: 915 },
  'nokia-g42': { width: 360, height: 800 },
  'iphone-12-mini': { width: 360, height: 780 },
  'galaxy-a54': { width: 360, height: 780 },
  'galaxy-a15': { width: 360, height: 780 },
  'oppo-a78': { width: 390, height: 844 },
  'asus-rog8': { width: 412, height: 915 },
};

const STATUS_LABELS: Record<string, string> = {
  idle: 'Aguardando',
  sdk_loading: 'Carregando SDK...',
  awaiting_permission: 'Aguardando permissao da camera',
  camera_active: 'Camera ativa',
  processing: 'Processando...',
  success: 'Captura concluida',
  error: 'Erro na captura',
};

interface CapturePageProps {
  embed?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const CapturePage: React.FC<CapturePageProps> = ({ embed, onSuccess, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isEmbed = embed || params.get('embed') === '1';
  const returnToParent = params.get('return') === 'parent';
  const isMinimal = params.get('minimal') === '1';
  const deviceId = params.get('device') ?? '';
  const deviceViewport = DEVICE_VIEWPORTS[deviceId] ?? null;

  const { status, error, result, initializeSession, cleanupSession, openLivenessCapture, resetCapture } = useUnico();
  const minimalAutoStartedRef = useRef(false);
  const minimalAttemptStartedRef = useRef(false);
  const minimalTerminalEventSentRef = useRef(false);

  useEffect(() => {
    initializeSession();
    return () => {
      cleanupSession();
    };
  }, [initializeSession, cleanupSession]);

  useEffect(() => {
    const syncViewportHeight = () => {
      const viewportHeight = isEmbed ? window.innerHeight : window.visualViewport?.height ?? window.innerHeight;
      const vh = viewportHeight * 0.01;
      document.documentElement.style.setProperty('--capture-vh', `${vh}px`);
    };

    syncViewportHeight();
    window.addEventListener('resize', syncViewportHeight);
    window.addEventListener('orientationchange', syncViewportHeight);
    if (!isEmbed) {
      window.visualViewport?.addEventListener('resize', syncViewportHeight);
    }

    return () => {
      window.removeEventListener('resize', syncViewportHeight);
      window.removeEventListener('orientationchange', syncViewportHeight);
      if (!isEmbed) {
        window.visualViewport?.removeEventListener('resize', syncViewportHeight);
      }
      document.documentElement.style.removeProperty('--capture-vh');
    };
  }, [isEmbed]);

  useEffect(() => {
    if (isEmbed || !deviceViewport) {
      document.documentElement.style.removeProperty('--capture-shell-width');
      document.documentElement.style.removeProperty('--capture-shell-height');
      return;
    }

    const syncDeviceShellSize = () => {
      const horizontalPadding = 32; // capture-page has 16px on each side
      const verticalPadding = 32; // capture-page top/bottom default without safe-area
      const availableWidth = Math.max(0, window.innerWidth - horizontalPadding);
      const availableHeight = Math.max(0, window.innerHeight - verticalPadding);
      const scale = Math.min(availableWidth / deviceViewport.width, availableHeight / deviceViewport.height, 1);

      const shellWidth = Math.max(0, Math.round(deviceViewport.width * scale));
      const shellHeight = Math.max(0, Math.round(deviceViewport.height * scale));

      document.documentElement.style.setProperty('--capture-shell-width', `${shellWidth}px`);
      document.documentElement.style.setProperty('--capture-shell-height', `${shellHeight}px`);
    };

    syncDeviceShellSize();
    window.addEventListener('resize', syncDeviceShellSize);
    window.addEventListener('orientationchange', syncDeviceShellSize);

    return () => {
      window.removeEventListener('resize', syncDeviceShellSize);
      window.removeEventListener('orientationchange', syncDeviceShellSize);
      document.documentElement.style.removeProperty('--capture-shell-width');
      document.documentElement.style.removeProperty('--capture-shell-height');
    };
  }, [isEmbed, deviceViewport]);

  useEffect(() => {
    if (status === 'success' && result) {
      if (returnToParent && window.parent && window.parent !== window) {
        minimalTerminalEventSentRef.current = true;
        window.parent.postMessage({ type: 'UNICO_CAPTURE_SUCCESS', result }, window.location.origin);
        return;
      }
      try {
        localStorage.setItem('unico_test_result', JSON.stringify(result));
      } catch {
        // ignore
      }
      if (onSuccess) {
        onSuccess();
        return;
      }
      navigate('/test-unico/result');
    }
  }, [status, result, navigate, onSuccess, returnToParent]);

  useEffect(() => {
    if (!isMinimal || minimalAutoStartedRef.current || status !== 'idle') return;
    minimalAutoStartedRef.current = true;
    void openLivenessCapture();
  }, [isMinimal, status, openLivenessCapture]);

  useEffect(() => {
    if (!isMinimal || !returnToParent || window.parent === window) return;

    const isActiveStatus =
      status === 'awaiting_permission' || status === 'camera_active' || status === 'processing';

    if (isActiveStatus) {
      minimalAttemptStartedRef.current = true;
      minimalTerminalEventSentRef.current = false;
    }

    if (
      status === 'error' &&
      error &&
      minimalAttemptStartedRef.current &&
      !minimalTerminalEventSentRef.current
    ) {
      minimalTerminalEventSentRef.current = true;
      window.parent.postMessage({ type: 'UNICO_CAPTURE_ERROR', error }, window.location.origin);
      return;
    }

    if (
      status === 'idle' &&
      minimalAutoStartedRef.current &&
      minimalAttemptStartedRef.current &&
      !result &&
      !error &&
      !minimalTerminalEventSentRef.current
    ) {
      minimalTerminalEventSentRef.current = true;
      minimalAttemptStartedRef.current = false;
      window.parent.postMessage({ type: 'UNICO_CAPTURE_CANCELED' }, window.location.origin);
    }
  }, [isMinimal, returnToParent, status, error, result]);

  const canCapture = status === 'idle' || status === 'error';
  const isActive = status === 'camera_active' || status === 'processing' || status === 'awaiting_permission';
  const isLoading = status === 'sdk_loading';

  if (isMinimal) {
    return (
      <div className={`capture-page capture-page-minimal ${isEmbed ? 'capture-page-embedded' : ''}`}>
        <div id="box-camera" className="capture-camera-slot capture-camera-slot-minimal" />

        {isLoading && (
          <div className="capture-minimal-overlay">
            <span>Abrindo camera...</span>
          </div>
        )}

        {error && <div className="capture-minimal-error">{error}</div>}
      </div>
    );
  }

  return (
    <div
      className={`capture-page ${isEmbed ? 'capture-page-embedded' : ''}${!isEmbed && deviceViewport ? ' capture-page-device' : ''}`}
    >
      <section className="capture-shell">
        <header className="capture-topbar">
          <h1>Selfie</h1>
        </header>

        <div className="capture-preview">
          <div id="box-camera" className="capture-camera-slot" />
          <div className="capture-mask" />
        </div>

        <footer className="capture-footer">
          <p className="capture-instruction">Enquadre seu rosto na moldura e tire a selfie.</p>

          <div className={`capture-status capture-status-${status}`}>
            <span className="capture-dot" />
            <span>{STATUS_LABELS[status] ?? status}</span>
          </div>

          {error && <div className="capture-error">{error}</div>}

          <div className="capture-actions">
            <button
              type="button"
              className="capture-shutter"
              onClick={openLivenessCapture}
              disabled={!canCapture || isActive || isLoading}
            >
              <span className="capture-shutter-ring">
                <span className="capture-shutter-core" />
              </span>
            </button>

            <button type="button" className="capture-reset" onClick={resetCapture} disabled={isActive}>
              Reiniciar sessao
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};
